import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { getProjectIconAndColor } from '@overlayqa/shared';
import { useProjects, useProject } from '../hooks/useProjects';
import { useIssue } from '../hooks/useTickets';
import { useAppSession } from '../hooks/useAppSession';
import { SelectDropdown } from './shared/SelectDropdown';

function ProjectIcon({ project, size = 20 }: { project: { name: string; icon?: string | null; iconColor?: string | null }; size?: number }) {
  const { icon, color } = getProjectIconAndColor({ name: project.name, icon: project.icon, iconColor: project.iconColor });
  const box = size + 4;
  const svgSize = size * 0.6;
  return (
    <span
      className="rounded-md hidden sm:flex items-center justify-center flex-shrink-0"
      style={{ width: box, height: box, backgroundColor: color.hex + '18' }}
    >
      <svg width={svgSize} height={svgSize} viewBox="0 0 24 24" fill={color.hex} aria-hidden="true">
        <path d={icon.path} />
      </svg>
    </span>
  );
}

export function ProjectBreadcrumb() {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: projectIdParam, ticketId } = useParams<{ projectId: string; ticketId: string }>();
  const { data: appSession } = useAppSession();

  // The issue drill-down is routed at `/issues/:ticketId` (no `:projectId`
  // param), so derive the project from the loaded ticket. `useIssue('')` is a
  // no-op off this route, and on it the query key matches the page's own fetch,
  // so this reads from cache without an extra request.
  const isIssueDetailPage = location.pathname.startsWith('/issues/') && !!ticketId;
  const { data: issue } = useIssue(isIssueDetailPage ? ticketId : '');
  const projectId = projectIdParam || (isIssueDetailPage ? issue?.projectId : undefined);

  const { data: currentProject } = useProject(projectId || '');
  const { data: projects } = useProjects(appSession?.activeTeamId, { includeCounts: true });

  // Determine if we're on a project-scoped page (project routes or the issue
  // drill-down, once we've resolved its project).
  const isProjectPage =
    (location.pathname.startsWith('/projects/') && !!projectIdParam) ||
    (isIssueDetailPage && !!projectId);

  // Only show breadcrumb on project-scoped pages
  if (!isProjectPage) return null;

  function handleSelectProject(id: string) {
    navigate(`/projects/${id}/issues`);
  }

  return (
    <div className="relative flex items-center gap-1 min-w-0">
      {/* Projects root link */}
      <Link
        to="/projects"
        className="hidden sm:flex shrink-0 items-center gap-2 text-sm text-primary-600 hover:text-primary-800 transition-colors"
      >
        <span className="font-semibold text-sm text-cap-trim">Projects</span>
      </Link>

      {/* Show current project breadcrumb when on a project page */}
      {isProjectPage && currentProject && (
        <>
          <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={2} color="currentColor" className="hidden sm:block w-3.5 h-3.5 shrink-0 text-gray-300" aria-hidden="true" />
          <SelectDropdown
            value={currentProject.id}
            onChange={handleSelectProject}
            aria-label={`Switch project: ${currentProject.name}`}
            variant="ghost"
            className="min-w-0 oq-text-caption h-[var(--oq-control-app-standard-height)] px-2 py-0 rounded-md max-w-[230px] hover:bg-gray-50 hover:border-gray-300"
            options={(projects ?? [currentProject]).map(project => ({
              value: project.id,
              label: project.name,
              leading: <ProjectIcon project={project} size={18} />,
            }))}
          />
        </>
      )}


    </div>
  );
}
